/**
 * /beat command handler.
 * Saves the current paragraph text as a StoryKeeper beat via sk-mcp.bebond.net.
 */

import type { BlockNoteEditor } from '@blocknote/core';

const SK_MCP_URL = import.meta.env.VITE_SK_MCP_URL || 'https://sk-mcp.bebond.net';

/**
 * Extract plain text from the current block (the block the cursor is in).
 */
function getCurrentBlockText(editor: BlockNoteEditor): string {
  const block = editor.getTextCursorPosition().block;
  if (!block || !block.content) return '';

  // block.content is an array of inline content items
  const contentArray = block.content as Array<{ type: string; text?: string }>;
  return contentArray
    .filter((item) => item.type === 'text' && item.text)
    .map((item) => item.text)
    .join('');
}

/**
 * Show a toast notification. Uses a simple DOM-based approach since the app
 * doesn't have a toast library installed.
 */
function showToast(message: string, type: 'success' | 'error' = 'success') {
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    bottom: 24px;
    right: 24px;
    padding: 12px 20px;
    background: ${type === 'success' ? '#37352f' : '#e03131'};
    color: #fff;
    border-radius: 8px;
    font-size: 14px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    transition: opacity 0.3s ease;
  `;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

/**
 * Save the current block text as a StoryKeeper beat.
 */
export async function saveBeat(editor: BlockNoteEditor): Promise<void> {
  const text = getCurrentBlockText(editor);

  if (!text.trim()) {
    showToast('Geen tekst om op te slaan', 'error');
    return;
  }

  try {
    const response = await fetch(`${SK_MCP_URL}/api/mcp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: crypto.randomUUID(),
        method: 'tools/call',
        params: {
          name: 'beat_write',
          arguments: {
            content: text,
            source: 'doc-editor',
            metadata: {
              origin: 'slash-command',
              timestamp: new Date().toISOString(),
            },
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`SK MCP returned ${response.status}`);
    }

    showToast('Beat opgeslagen');
  } catch (err) {
    console.error('Failed to save beat:', err);
    showToast('Beat opslaan mislukt', 'error');
  }
}
